<template>
  <span
    :class="rootClasses"
   
  >
    <span
      v-if="hasCustomRing"
      :class="['absolute inset-0 rounded-full opacity-20', colorClass]"
     
    />
    <span :class="dotClasses" />
  </span>
</template>

<script setup lang="ts">
/**
 * Some notes: I'd eventually want to start using tailwind merge to better
 * handle conditional classes, but also be able to specify "conflicting" classes
 * from the parent and merge them in gracfully (i.e. `absolute` from parent and
 * `relative` in the component's root).
 */
import type { DotRingStyle, DotSize, DotVariant } from './types'

const props = withDefaults(defineProps<{
  size?: DotSize
  ringStyle?: DotRingStyle
  variant?: DotVariant
  colorClass?: string // A valid Tailwind classname (e.g. 'bg-[#ff0000]')
  isDisabled?: boolean
}>(), {
  size: 'md',
  ringStyle: 'none',
  variant: 'info',
})

const hasCustomRing = computed(() => (
  props.ringStyle === 'muted'
  && !!props.colorClass
  && !props.isDisabled
))

const rootSizeClasses = computed(() => ([
  'relative inline-flex justify-center items-center rounded-full',
  {
    'p-0.5': props.ringStyle === 'white',
    'p-1': props.ringStyle === 'muted',
  },
]))

const rootColorClasses = computed(() => {
  if (props.ringStyle === 'none') {
    return
  }

  if (props.ringStyle === 'white') {
    return 'bg-white'
  }

  if (props.isDisabled) {
    return 'bg-surface-disabled'
  }

  if (props.colorClass) {
    return
  }

  return {
    'bg-surface-muted-success': props.variant === 'success',
    'bg-surface-muted-danger': props.variant === 'danger',
    'bg-surface-muted-warning': props.variant === 'warning',
    'bg-surface-muted-info': props.variant === 'info',
  }
})

const rootClasses = computed(() => ([
  rootSizeClasses.value,
  rootColorClasses.value,
]))

const dotSizeClasses = computed(() => ([
  'relative rounded-full',
  {
    'size-1.5': props.size === 'xs',
    'size-2': props.size === 'sm',
    'size-2.5': props.size === 'md',
    'size-3': props.size === 'lg',
    'size-3.5': props.size === 'xl',
  },
]))

const dotColorClasses = computed(() => {
  if (props.isDisabled) {
    return 'bg-text-disabled'
  }

  if (props.colorClass) {
    return props.colorClass
  }

  return {
    'bg-text-success': props.variant === 'success',
    'bg-text-danger': props.variant === 'danger',
    'bg-text-warning': props.variant === 'warning',
    'bg-text-info': props.variant === 'info',
  }
})

const dotClasses = computed(() => ([
  dotSizeClasses.value,
  dotColorClasses.value,
]))
</script>
